import React, { useEffect, useRef } from "react";

// ─── Wordmark interactivo "Ulture" (puntitos + logo remolino, canvas) ───
// Extraído TAL CUAL de FooterLanding.tsx para usarlo también al final de la home
// nueva (la del diseño de Claude Design): mismo panel, misma lógica, mismo
// arrastre táctil y mismo deslizamiento al llegar al fondo de la página.
// [ULTURE] Rendimiento y accesibilidad, sin cambiar el aspecto ni la interacción (cada cambio va marcado [ULTURE]):
//  · Fuera de pantalla no hace nada: un IntersectionObserver (±240 px, el margen de antes) arranca y para el bucle y el
//    deslizamiento del panel; ya no se mide nada en cada scroll ni en cada movimiento del ratón lejos del pie.
//  · Sin ratón ni dedo no se mide el canvas (las medidas solo sirven para la posición del puntero).
//  · En reposo (puntero fuera del alcance de los puntos y quieto, sin dedo) el brillo de fondo se pinta a 30 fps; con el
//    puntero cerca o moviéndose, en cada fotograma con la misma cuenta que antes (seguimiento 0,18 por fotograma).
//  · «Reducir movimiento»: un fotograma fijo y el panel ya subido. Los canvas son decorativos (aria-hidden).

const disp = "var(--font-display, 'Poppins'), sans-serif";

// ══ Wordmark de puntitos + logo remolino (canvas) — verbatim de la referencia ══
function useWordmarkDots(
  wmWrapRef: React.RefObject<HTMLDivElement>,
  wmRef: React.RefObject<HTMLDivElement>,
  wmCanvasRef: React.RefObject<HTMLCanvasElement>,
  wmMarkRef: React.RefObject<HTMLCanvasElement>,
  wmRowRef: React.RefObject<HTMLDivElement>
) {
  useEffect(() => {
    const HOT = [[11, 136, 196], [8, 142, 154], [20, 169, 87], [19, 49, 72], [126, 139, 60], [62, 92, 42], [46, 110, 150]];
    const BASE = [150, 173, 150];
    let dots: any[] = [];
    let textW = 0, textH = 0;
    const mouse = { tx: -9999, ty: -9999, x: -9999, y: -9999 };
    let raf: number | null = null;
    // [ULTURE] En reposo el bucle duerme con un temporizador hasta el siguiente fotograma de reposo (antes pedía un
    // requestAnimationFrame en cada refresco de pantalla, 60–120 por segundo, para dibujar 15). wake() lo despierta ya.
    let sleep: ReturnType<typeof setTimeout> | null = null;
    let wake: (() => void) | null = null;
    let logoPtsRaw: number[][] | null = null;
    let logoDots: any[] = [];
    let logoSize = 0, logoBase = 0;
    let disposed = false;
    // [ULTURE] «Reducir movimiento»: un fotograma fijo, sin bucle, y el panel ya subido sin transición.
    const reduceMotion = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    // [ULTURE] ¿Está el pie cerca de la pantalla? Lo dice un IntersectionObserver (abajo); sin IO, siempre «sí» y el bucle
    // mide como antes.
    const hasIO = "IntersectionObserver" in window;
    let inView = !hasIO;
    // [ULTURE] Reposo a 30 fps: últimas medidas de los canvas (para saber si el puntero alcanza los puntos sin medir de
    // nuevo), fotogramas desde el último dibujo (el seguimiento del puntero se aplica una vez por fotograma, como antes)
    // y momento del último dibujo.
    let lastRect: DOMRect | null = null, lastLRect: DOMRect | null = null, reachText = 0;
    let ticks = 1, lastDraw = -1e9, touching = false, armed = !hasIO, lastScroll = -1e9;
    // [ULTURE] En pantallas táctiles (móvil/tableta), en reposo a 15 fps: el brillo va igual de lento (depende del tiempo, no
    // de los fotogramas) y un móvil barato no se pasa el rato dibujando cientos de puntos.
    let coarse = false; try { coarse = window.matchMedia("(pointer: coarse)").matches; } catch (e) { /* sin matchMedia: 30 fps */ }
    const IDLE_STEP = 1000 / (coarse ? 15 : 30) - 2;

    const buildWmDots = () => {
      const cv = wmCanvasRef.current, wrap = wmWrapRef.current, mark = wmMarkRef.current;
      if (!cv || !wrap) return;
      const isMobile = window.innerWidth < 760;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const avail = Math.min(wrap.clientWidth - (isMobile ? 32 : 48), 1280);
      if (avail <= 0) return;
      const m = document.createElement("canvas").getContext("2d")!;
      const fam = "'Poppins', sans-serif";
      m.font = "800 100px " + fam;
      const ratio = m.measureText("Ulture").width / 100;
      const gap = isMobile ? 10 : 22;
      const markRatio = 0.82;
      let fontPx = (avail - gap) / (ratio + markRatio);
      fontPx = Math.min(fontPx, isMobile ? 150 : 340);
      fontPx = Math.max(fontPx, 40);
      fontPx *= 0.93;
      const off = document.createElement("canvas");
      const octx = off.getContext("2d")!;
      octx.font = "800 " + fontPx + "px " + fam;
      const tw = Math.ceil(octx.measureText("Ulture").width);
      const th = Math.ceil(fontPx * 1.02);
      off.width = tw; off.height = th;
      octx.font = "800 " + fontPx + "px " + fam;
      octx.fillStyle = "#000";
      octx.textBaseline = "alphabetic";
      octx.fillText("Ulture", 0, fontPx * 0.82);
      const data = octx.getImageData(0, 0, tw, th).data;
      const step = isMobile ? 6 : Math.max(7, Math.round(fontPx / 34));
      const ds: any[] = [];
      for (let y = 0; y < th; y += step) {
        for (let x = 0; x < tw; x += step) {
          const a = data[(y * tw + x) * 4 + 3];
          if (a > 130) {
            const idx = ds.length;
            ds.push({
              x: x + step * 0.5, y: y + step * 0.5, r: step * 0.34,
              hot: HOT[(x * 7 + y * 3) % HOT.length],
              ph: (idx % 40) / 40,
              tsp: 0.7 + ((idx * 7) % 17) / 17 * 1.5,
              so: ((idx * 13) % 100) / 100,
            });
          }
        }
      }
      dots = ds; textW = tw; textH = th;
      cv.width = tw * dpr; cv.height = th * dpr;
      cv.style.width = tw + "px"; cv.style.height = th + "px";
      cv.getContext("2d")!.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (mark && logoPtsRaw) {
        const size = Math.round(fontPx * 0.82);
        const ls = size / 200;
        mark.width = size * dpr; mark.height = size * dpr;
        mark.style.width = size + "px"; mark.style.height = size + "px";
        mark.getContext("2d")!.setTransform(dpr, 0, 0, dpr, 0, 0);
        logoSize = size;
        // [ULTURE] Con el logo pequeño (móvil, ~60 px) la rejilla de 3 px del original quedaba a menos de 1 px en pantalla:
        // unos 2.000 puntos por fotograma que se veían como una mancha. Se toma 1 de cada k (k ≥ 1) para que queden a
        // ≥ 1,8 px en pantalla, con el radio en proporción; en ordenador (logo grande) k = 1, igual que antes.
        const k = Math.max(1, Math.ceil(1.8 / (3 * ls)));
        logoBase = 3 * k * ls * 0.56;
        logoDots = logoPtsRaw.filter(([px, py]) => (px / 3) % k === 0 && (py / 3) % k === 0).map(([px, py, r, g, b]) => ({
          x: px * ls, y: py * ls,
          d: Math.hypot(px - 100, py - 100),
          cr: r, cg: g, cb: b,
          col: "rgb(" + r + "," + g + "," + b + ")",
        }));
      }
      ticks++; // [ULTURE] este dibujo cuenta como un fotograma más del seguimiento del puntero (como antes)
      drawWm(0);
      ensureLoop();
    };

    const drawLogoDots = (tt: number) => {
      const lcv = wmMarkRef.current;
      if (!lcv || !logoDots.length || !logoSize) return;
      const size = logoSize;
      const lctx = lcv.getContext("2d")!;
      lctx.clearRect(0, 0, size, size);
      const has = mouse.tx > -9000;
      // [ULTURE] la medida solo sirve para el puntero: sin puntero no se mide
      const lrect = has ? lcv.getBoundingClientRect() : null;
      lastLRect = lrect;
      const mx = lrect ? mouse.x - lrect.left : 0;
      const my = lrect ? mouse.y - lrect.top : 0;
      const R = size * 0.5, R2 = R * R;
      const spin = tt * 4;
      const ca = Math.cos(spin), sa = Math.sin(spin);
      const swirlAmp = size * 0.062;
      const base = logoBase;
      for (let i = 0; i < logoDots.length; i++) {
        const p = logoDots[i];
        const w = 0.5 + 0.5 * Math.sin(p.d * 0.08 - tt * 1.8);
        let px2 = p.x, py2 = p.y, light = 0, grow = 0;
        if (has) {
          const dx = p.x - mx, dy = p.y - my;
          const d2 = dx * dx + dy * dy;
          if (d2 < R2) {
            const dd = Math.sqrt(d2) || 1;
            const mm = 1 - dd / R;
            const swirl = mm * mm * swirlAmp;
            const txv = (-dy / dd) * ca - (dx / dd) * sa * 0.3;
            const tyv = (dx / dd) * ca - (dy / dd) * sa * 0.3;
            px2 += txv * swirl + (dx / dd) * mm * 3;
            py2 += tyv * swirl + (dy / dd) * mm * 3;
            light = mm * 0.7;
            grow = mm * 0.5;
          }
        }
        const rad = base * (0.5 + 0.5 * w) * (1 + grow);
        if (light > 0.02) {
          const cr = Math.round(p.cr + (255 - p.cr) * light);
          const cg = Math.round(p.cg + (255 - p.cg) * light * 0.75);
          const cb = Math.round(p.cb + (255 - p.cb) * light * 0.45);
          lctx.fillStyle = "rgb(" + cr + "," + cg + "," + cb + ")";
        } else {
          lctx.fillStyle = p.col;
        }
        lctx.globalAlpha = 0.5 + 0.5 * Math.min(1, w + light);
        lctx.beginPath();
        lctx.arc(px2, py2, rad, 0, 6.2832);
        lctx.fill();
      }
      lctx.globalAlpha = 1;
    };

    const drawWm = (t: number) => {
      const cv = wmCanvasRef.current;
      if (!cv || !dots.length) return;
      const ctx = cv.getContext("2d")!;
      ctx.clearRect(0, 0, textW, textH);
      // [ULTURE] 0,18 por fotograma, como antes; si en reposo se saltó alguno, se aplica una vez por fotograma pasado
      // (con el puntero quieto da lo mismo que dibujando todos)
      for (let i = 0; i < ticks; i++) {
        mouse.x += (mouse.tx - mouse.x) * 0.18;
        mouse.y += (mouse.ty - mouse.y) * 0.18;
      }
      ticks = 0;
      const hasMouse = mouse.tx > -9000;
      // [ULTURE] la medida solo sirve para el puntero: sin puntero no se mide
      const rect = hasMouse ? cv.getBoundingClientRect() : null;
      lastRect = rect;
      const mx = rect ? mouse.x - rect.left : 0;
      const my = rect ? mouse.y - rect.top : 0;
      const W = textW, H = textH;
      const R = Math.max(150, H * 0.62);
      reachText = R; // [ULTURE]
      const R2 = R * R;
      const b = BASE;
      const tt = (t || 0) * 0.001;
      const bx1 = (0.5 + 0.40 * Math.sin(tt * 0.23) * Math.cos(tt * 0.10)) * W;
      const by1 = (0.5 + 0.32 * Math.sin(tt * 0.31 + 1.2)) * H;
      const bx2 = (0.5 + 0.44 * Math.cos(tt * 0.18 + 2.0)) * W;
      const by2 = (0.5 + 0.28 * Math.sin(tt * 0.27 + 3.4)) * H;
      const BR = Math.max(130, H * 0.52);
      const BR2 = BR * BR;
      for (let i = 0; i < dots.length; i++) {
        const d = dots[i];
        let k = 0;
        if (hasMouse) {
          const dx = d.x - mx, dy = d.y - my, dd = dx * dx + dy * dy;
          if (dd < R2) { k = 1 - Math.sqrt(dd) / R; k = k * k; }
        }
        let ka = 0;
        { const dx = d.x - bx1, dy = d.y - by1, dd = dx * dx + dy * dy;
          if (dd < BR2) { const q = 1 - Math.sqrt(dd) / BR; ka = q * q; } }
        { const dx = d.x - bx2, dy = d.y - by2, dd = dx * dx + dy * dy;
          if (dd < BR2) { const q = 1 - Math.sqrt(dd) / BR; if (q * q > ka) ka = q * q; } }
        const kk = Math.max(k, ka * 0.6);
        const spark = Math.pow(0.5 + 0.5 * Math.sin(tt * d.tsp + d.ph * 6.28 + d.so * 6.28), 2.4);
        const h = d.hot;
        const cr = b[0] + (h[0] - b[0]) * kk;
        const cg = b[1] + (h[1] - b[1]) * kk;
        const cb = b[2] + (h[2] - b[2]) * kk;
        const alpha = 0.24 + 0.20 * spark + 0.52 * kk;
        const rad = d.r * (0.84 + 0.16 * spark + kk * 1.0);
        let ox = 0, oy = 0;
        if (hasMouse && k > 0) {
          const dx = d.x - mx, dy = d.y - my, dist = Math.sqrt(dx * dx + dy * dy) || 1, push = k * 5;
          ox = (dx / dist) * push; oy = (dy / dist) * push;
        }
        ctx.beginPath();
        ctx.fillStyle = "rgba(" + (cr | 0) + "," + (cg | 0) + "," + (cb | 0) + "," + alpha.toFixed(3) + ")";
        ctx.arc(d.x + ox, d.y + oy, rad, 0, 6.2832);
        ctx.fill();
      }
      drawLogoDots(tt);
    };

    // [ULTURE] ¿Puede el puntero tocar algún punto? (dentro del alcance del foco de la palabra o del remolino del logo,
    // con las medidas del último fotograma dibujado). Si no, el dibujo es el mismo que sin puntero.
    const near = (r: DOMRect | null, reach: number) =>
      !!r && mouse.x > r.left - reach && mouse.x < r.right + reach && mouse.y > r.top - reach && mouse.y < r.bottom + reach;
    const active = () => {
      if (touching) return true;
      if (mouse.tx < -9000) return false;
      if (Math.abs(mouse.tx - mouse.x) > 0.25 || Math.abs(mouse.ty - mouse.y) > 0.25) return true; // el foco aún se mueve
      if (!lastRect) return true;
      return near(lastRect, reachText + 4) || near(lastLRect, logoSize * 0.5 + 4);
    };

    const ensureLoop = () => {
      if (sleep && wake) { wake(); return; } // [ULTURE] dormido: despertar ya (dedo, puntero, scroll)
      if (raf || disposed || reduceMotion) return; // [ULTURE] reduceMotion
      if (!inView || !armed) return; // [ULTURE] fuera de pantalla no arranca (lo arranca el IntersectionObserver / el scroll)
      const loop = (t: number) => {
        // rendimiento: comprobar visibilidad ANTES de dibujar — así el scroll
        // lejos del footer no paga el redibujado completo del canvas
        let visible = inView && armed;
        if (!hasIO) { // [ULTURE] sin IntersectionObserver, la medida de siempre
          const cv = wmCanvasRef.current;
          visible = false;
          if (cv) { const r = cv.getBoundingClientRect(); visible = r.bottom > -240 && r.top < window.innerHeight + 240; }
        }
        if (!visible || disposed) { raf = null; return; }
        ticks++;
        // [ULTURE] Pantallas táctiles: durante el scroll no se redibuja el brillo (el panel sigue deslizándose; el hilo
        // principal queda para el scroll)
        const scrolling = coarse && !touching && performance.now() - lastScroll < 160;
        // [ULTURE] con el puntero cerca o moviéndose (o el dedo encima), cada fotograma; en reposo, a 30 fps (15 en táctil)
        const act = active();
        if (!scrolling && (act || t - lastDraw >= IDLE_STEP)) { drawWm(t); lastDraw = t; }
        if (act && !scrolling) { raf = requestAnimationFrame(loop); return; }
        raf = -1; // [ULTURE] dormido (ensureLoop no arranca otro bucle)
        wake = () => { if (sleep) clearTimeout(sleep); sleep = null; raf = requestAnimationFrame(loop); };
        sleep = setTimeout(() => { sleep = null; raf = requestAnimationFrame(loop); }, scrolling ? 160 : Math.max(8, IDLE_STEP - (performance.now() - lastDraw)));
      };
      raf = requestAnimationFrame(loop);
    };

    const loadLogoPts = () => {
      const img = new Image();
      img.onload = () => {
        const N = 200;
        const c = document.createElement("canvas"); c.width = N; c.height = N;
        const x = c.getContext("2d")!;
        x.drawImage(img, 0, 0, N, N);
        const d = x.getImageData(0, 0, N, N).data;
        const pts: number[][] = [];
        for (let py = 0; py < N; py += 3) for (let px = 0; px < N; px += 3) {
          const k = (py * N + px) * 4;
          if (d[k + 3] > 140) pts.push([px, py, d[k], d[k + 1], d[k + 2]]);
        }
        logoPtsRaw = pts;
        buildWmDots();
      };
      img.src = "/landing/assets/ulture-mark.png";
    };

    // Panel blanco que se desliza al llegar al fondo (_updateWordmark, línea 2074)
    const updateWordmark = () => {
      const el = wmRef.current;
      const wrap = wmWrapRef.current;
      if (!el || !wrap) return;
      if (reduceMotion) { el.style.transition = "none"; el.style.transform = "none"; return; } // [ULTURE]
      const vh = window.innerHeight || 800;
      const wr = wrap.getBoundingClientRect(); // [ULTURE] el rect entero (antes solo .top): sirve también para «armed»
      const rTop = wr.top;
      const H = wrap.offsetHeight || 1;
      let p = Math.max(0, Math.min(1, (vh - rTop) / H));
      p = Math.max(0, Math.min(1, (p - 0.34) / 0.66));
      const ease = 1 - Math.pow(1 - p, 3);
      el.style.transform = "translateY(" + ((1 - ease) * 100).toFixed(2) + "%)";
      // [ULTURE] ¿Asoma ya el panel (con su desplazamiento) a menos de 240 px de la pantalla? Es la condición de antes
      // («el canvas a ±240 px»), calculada aquí sin medir el canvas en cada fotograma: mientras el panel aún está
      // escondido abajo, no se dibuja.
      armed = rTop + (1 - ease) * wr.height < vh + 240 && wr.bottom > -240;
    };

    const onScroll = () => { lastScroll = performance.now(); if (!inView) return; /* [ULTURE] lejos del pie no hay nada que hacer */ updateWordmark(); ensureLoop(); };
    const onResize = () => { buildWmDots(); updateWordmark(); ensureLoop(); /* [ULTURE] por si al cambiar de tamaño el panel asoma */ };
    const onMouse = (e: MouseEvent) => {
      mouse.tx = e.clientX; mouse.ty = e.clientY;
      if (mouse.x < -9000) { mouse.x = e.clientX; mouse.y = e.clientY; }
      ensureLoop();
    };
    // Touch (móvil/tablet): arrastra el dedo por el wordmark para animarlo igual
    // que con el cursor. La fila lleva touchAction:"none" (en el JSX) para que el
    // arrastre NO haga scroll de la página — así el rect del canvas no se mueve y
    // el reveal sigue al dedo sin saltos. Al soltar, el punto se desliza fuera
    // del canvas y se apaga suave.
    const onTouchStart = (e: TouchEvent) => {
      const tch = e.touches[0]; if (!tch) return;
      // fija el spotlight al dedo de inmediato (sin "fly-in" desde la última posición)
      mouse.tx = mouse.x = tch.clientX;
      mouse.ty = mouse.y = tch.clientY;
      touching = true; // [ULTURE]
      ensureLoop();
    };
    const onTouchMove = (e: TouchEvent) => {
      const tch = e.touches[0]; if (!tch) return;
      mouse.tx = tch.clientX; mouse.ty = tch.clientY;
      ensureLoop();
    };
    const onTouchOff = () => { mouse.ty = -100000; touching = false; /* [ULTURE] */ ensureLoop(); };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", onMouse, { passive: true });
    const touchTarget = wmRowRef.current;
    if (touchTarget) {
      touchTarget.addEventListener("touchstart", onTouchStart, { passive: true });
      touchTarget.addEventListener("touchmove", onTouchMove, { passive: true });
      touchTarget.addEventListener("touchend", onTouchOff, { passive: true });
      touchTarget.addEventListener("touchcancel", onTouchOff, { passive: true });
    }
    // [ULTURE] Arranca y para el bucle y el deslizamiento según el pie esté cerca de la pantalla (±240 px, como antes).
    let io: IntersectionObserver | null = null;
    if (hasIO && wmWrapRef.current) {
      io = new IntersectionObserver((es) => {
        const e = es[es.length - 1];
        inView = !!e && e.isIntersecting;
        updateWordmark();
        if (inView) ensureLoop();
      }, { rootMargin: "240px 0px 240px 0px" });
      io.observe(wmWrapRef.current);
    } else inView = true;
    requestAnimationFrame(updateWordmark);
    loadLogoPts();
    if (document.fonts && (document.fonts as any).ready) {
      (document.fonts as any).ready.then(() => { if (!disposed) buildWmDots(); });
    }
    const t1 = setTimeout(buildWmDots, 400);
    const t2 = setTimeout(buildWmDots, 1400);

    return () => {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      if (sleep) clearTimeout(sleep); // [ULTURE]
      if (io) io.disconnect(); // [ULTURE]
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMouse);
      if (touchTarget) {
        touchTarget.removeEventListener("touchstart", onTouchStart);
        touchTarget.removeEventListener("touchmove", onTouchMove);
        touchTarget.removeEventListener("touchend", onTouchOff);
        touchTarget.removeEventListener("touchcancel", onTouchOff);
      }
    };
  }, []);
}

export default function InteractiveWordmark({ isMobile, marginTop = "clamp(34px, 5vw, 68px)" }: { isMobile: boolean; marginTop?: string }) {
  const wmWrapRef = useRef<HTMLDivElement>(null);
  const wmRef = useRef<HTMLDivElement>(null);
  const wmCanvasRef = useRef<HTMLCanvasElement>(null);
  const wmMarkRef = useRef<HTMLCanvasElement>(null);
  const wmRowRef = useRef<HTMLDivElement>(null);
  useWordmarkDots(wmWrapRef, wmRef, wmCanvasRef, wmMarkRef, wmRowRef);
  return (
      <div ref={wmWrapRef} style={{ position: "relative", width: "100%", overflow: "hidden", marginTop }}>
        <div
          ref={wmRef}
          style={{
            background: "#EEF4F8",
            borderRadius: "clamp(26px, 4vw, 46px) clamp(26px, 4vw, 46px) 0 0",
            // Móvil: panel ~30% más bajo (menos padding + wordmark más pequeño).
            padding: isMobile ? "24px 10px 58px" : "clamp(29px, 4.34vw, 70px) 20px clamp(25px, 3.61vw, 56px)",
            boxSizing: "border-box", willChange: "transform", transition: "transform .14s linear",
          }}
        >
          <div ref={wmRowRef} style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: "0.1em",
            fontFamily: disp, fontWeight: 800,
            fontSize: isMobile ? "20vw" : "clamp(82px, 23vw, 440px)", lineHeight: 0.9, letterSpacing: "-0.045em",
            color: "#0B1B28", whiteSpace: "nowrap", userSelect: "none", paddingTop: 0, marginBottom: 0,
            // El arrastre táctil sobre el wordmark no debe hacer scroll de la
            // página (si no, el rect del canvas se movería y el reveal saltaría).
            touchAction: "none",
          }}>
            {/* [ULTURE] decorativos: aria-hidden (antes, dos aria-label="Ulture" sin role) */}
            <canvas ref={wmMarkRef} aria-hidden="true" style={{ display: "block", flexShrink: 0 }} />
            <canvas ref={wmCanvasRef} aria-hidden="true" style={{ display: "block" }} />
          </div>
        </div>
      </div>
  );
}
